/**
 * Prompt Validation Hook
 * Feature: 015-custom-prompts
 * Date: 2026-01-04
 *
 * Provides debounced validation for custom prompt templates
 * - Character count (50-5000)
 * - Unknown variable warnings
 * - Real-time validation feedback
 */

import { useState, useEffect, useMemo } from 'react';
import {
  IPromptValidationResult,
  PROMPT_VALIDATION,
  KNOWN_VARIABLES,
  KnownVariable,
} from '@/@types/prompts';

interface IUsePromptValidationOptions {
  debounceMs?: number;
}

/**
 * Custom hook for validating prompt templates
 *
 * Validates:
 * - Minimum length (50 characters after trim)
 * - Maximum length (5000 characters)
 * - Unknown Handlebars variables (non-blocking warning)
 *
 * @param promptText - The prompt text to validate
 * @param options - Configuration options (debounce delay)
 * @returns Validation result with errors, warnings, and metadata
 *
 * @example
 * const { isValid, errors, warnings, length } = usePromptValidation(promptText);
 */
export const usePromptValidation = (
  promptText: string,
  options: IUsePromptValidationOptions = {}
): IPromptValidationResult => {
  const { debounceMs = PROMPT_VALIDATION.DEBOUNCE_MS } = options;

  // State for debounced prompt text
  const [debouncedPrompt, setDebouncedPrompt] = useState(promptText);

  // Debounce the prompt text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPrompt(promptText);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [promptText, debounceMs]);

  // Memoize validation result
  const validationResult = useMemo((): IPromptValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Trim for validation
    const trimmed = debouncedPrompt.trim();
    const length = trimmed.length;

    // Validation: Empty check
    if (length === 0) {
      return {
        isValid: false,
        errors: ['Prompt cannot be empty'],
        warnings: [],
        length: 0,
      };
    }

    // Validation: Minimum length
    if (length < PROMPT_VALIDATION.MIN_LENGTH) {
      errors.push(
        `Prompt must be at least ${PROMPT_VALIDATION.MIN_LENGTH} characters (currently ${length})`
      );
    }

    // Validation: Maximum length (use original length, not trimmed)
    if (debouncedPrompt.length > PROMPT_VALIDATION.MAX_LENGTH) {
      errors.push(
        `Prompt cannot exceed ${PROMPT_VALIDATION.MAX_LENGTH} characters (currently ${debouncedPrompt.length})`
      );
    }

    // Warning: Unknown variables
    const unknownVariables = findUnknownVariables(trimmed);
    if (unknownVariables.length > 0) {
      warnings.push(
        `Unknown variables detected: ${unknownVariables.join(', ')}. These will not be substituted.`
      );
    }

    const isValid = errors.length === 0;

    return {
      isValid,
      errors,
      warnings,
      length,
    };
  }, [debouncedPrompt]);

  return validationResult;
};

/**
 * Find unknown Handlebars variables in prompt text
 *
 * Extracts {{variable}} patterns and checks against known variables
 *
 * @param promptText - The prompt text to analyze
 * @returns Array of unknown variable names
 */
function findUnknownVariables(promptText: string): string[] {
  // Extract all {{variable}} patterns
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const matches = promptText.matchAll(variablePattern);

  const unknownVars = new Set<string>();
  const knownVarNames = new Set(KNOWN_VARIABLES.map((v) => v.name));

  for (const match of matches) {
    const varName = match[1].trim();

    // Skip Handlebars control structures (#if, /if, #each, etc.)
    if (varName.startsWith('#') || varName.startsWith('/')) {
      continue;
    }

    // Extract base variable name from conditional blocks
    // e.g., "if company_name" → "company_name"
    const baseVarName = varName.split(' ')[0] as KnownVariable;

    // Check if variable is known
    if (!knownVarNames.has(baseVarName)) {
      unknownVars.add(varName);
    }
  }

  return Array.from(unknownVars).sort();
}
